import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { Checkbox } from "@ui/Checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@ui/Form";
import { themeAtom } from "@/state/theme";
import { COMPANY_CONFIG } from "@/constants/companyData";
import { useMultiStepForm } from "@/hooks";
import { getBooleanEnumValue } from "@/utils/booleanEnum";

const Consents: FC = () => {
  const { t } = useTranslation();
  const { form } = useMultiStepForm();
  const theme = useAtomValue(themeAtom);
  const companyName = COMPANY_CONFIG[theme].title;

  const consents = [
    { name: "privacyPolicyRodo", required: true },
    { name: "dataAccuracyResponsibilityConsent", required: true },
    { name: "marketingEmailConsent", required: false },
    { name: "marketingPhoneConsent", required: false },
    { name: "newsletterConsent", required: false },
    { name: "dataProcessingConsent", required: true },
  ] as const;

  const watchedConsents = consents.map(({ name }) => form.watch(`step5.${name}` as const));

  const allChecked = watchedConsents.every(value => getBooleanEnumValue(value));

  const handleSelectAll = (checked: boolean) => {
    const nextValue = checked ? "true" : "false";
    consents.forEach(({ name }) => {
      form.setValue(`step5.${name}` as const, nextValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  return (
    <div className="space-y-3" data-test="consents-section">
      <p className="pb-1 text-base font-bold">{t("step5.q2")}</p>
      <FormItem className="flex items-start gap-3 px-4" data-test="consent-select-all">
        <FormControl>
          <Checkbox
            className="my-1"
            checked={allChecked}
            onCheckedChange={checked => handleSelectAll(checked === true)}
            dataTest="consent-select-all-checkbox"
          />
        </FormControl>
        <FormLabel className="cursor-pointer font-normal">{t("step5.q2_selectAll")}</FormLabel>
      </FormItem>
      <div className="space-y-3 px-4" data-test="consents-list">
        {consents.map(({ name, required }) => (
          <FormField
            key={name}
            control={form.control}
            name={`step5.${name}`}
            render={({ field }) => (
              <FormItem className="flex items-start gap-3">
                <FormControl>
                  <Checkbox
                    className="my-1"
                    checked={getBooleanEnumValue(field.value)}
                    onCheckedChange={checked => field.onChange(checked ? "true" : "false")}
                    dataTest={`${name}-checkbox`}
                  />
                </FormControl>
                <div>
                  <FormLabel className="cursor-pointer font-normal">
                    {required && "* "}
                    {t(`step5.q2_${name}`, { companyName })}
                  </FormLabel>
                  <FormMessage data-test={`${name}-error`} />
                </div>
              </FormItem>
            )}
          />
        ))}
      </div>
      <p className="text-xs font-extralight">{t("step5.agreementNote")}</p>
    </div>
  );
};

export default Consents;
